// Confirmación de contraseña para las operaciones sensibles del ADMIN (alta de cuentas, revocar, accesos).
// El servidor exige haberla confirmado en los últimos 5 minutos; si responde REAUTHENTICATION_REQUIRED, se
// pide aquí y la acción se repite. La contraseña no se guarda: solo viaja en POST /auth/reauthenticate.
import { type FormEvent, useCallback, useEffect, useId, useRef, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { errorText } from '../../lib/copy';
import styles from '../../ui/ConfirmDialog.module.css';
import { IconLock } from '../../ui/icons';
import { PasswordField } from './PasswordField';

function ReauthDialog({ open, onConfirmed, onCancel }: { open: boolean; onConfirmed: () => void; onCancel: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    if (open && !element.open) {
      setPassword('');
      setError(null);
      element.showModal();
    }
    if (!open && element.open) element.close();
  }, [open]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!password || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api<void>('POST', '/auth/reauthenticate', { body: { password } });
      setPassword('');
      onConfirmed();
    } catch (failure) {
      setError(failure instanceof ApiError && failure.code === 'REAUTHENTICATION_FAILED' ? 'La contraseña no es correcta.' : errorText(failure));
    } finally {
      setBusy(false);
    }
  };

  // Al cancelar no queda la contraseña en memoria ni en el campo oculto.
  const cancel = () => {
    if (busy) return;
    setPassword('');
    onCancel();
  };

  return (
    <dialog ref={dialog} className={styles.dialog} aria-labelledby={titleId}
      onCancel={(event) => { event.preventDefault(); cancel(); }}
      onClick={(event) => { if (event.target === event.currentTarget) cancel(); }}>
      <form className={styles.card} onSubmit={(event) => void submit(event)} noValidate>
        <span className={styles.icon} aria-hidden="true"><IconLock /></span>
        <h2 id={titleId} className={styles.title}>Confirma tu contraseña</h2>
        <div className={styles.body}><p>Es una operación de administración. Hilo te la volverá a pedir dentro de 5 minutos.</p></div>
        <div className={styles.field}>
          <PasswordField label="Contraseña" value={password} onChange={setPassword} autoComplete="current-password" invalid={error !== null} />
        </div>
        {error && <p className={styles.error} role="alert">{error}</p>}
        <div className={styles.actions}>
          <button type="button" className={`btn ${styles.cancel}`} disabled={busy} onClick={cancel}>Cancelar</button>
          <button type="submit" className="btn btn-primary" disabled={busy || !password} aria-busy={busy}>{busy ? 'Comprobando…' : 'Confirmar'}</button>
        </div>
      </form>
    </dialog>
  );
}

/**
 * Ejecuta una acción sensible. Si el servidor pide confirmar la contraseña, abre el diálogo y la repite al
 * confirmar; `undefined` si la persona cancela. Los demás errores llegan al llamante.
 */
export function useSensitiveAction() {
  const pending = useRef<{ retry: () => void; cancel: () => void } | null>(null);
  const [open, setOpen] = useState(false);

  const perform = useCallback(async <T,>(action: () => Promise<T>): Promise<T | undefined> => {
    try {
      return await action();
    } catch (error) {
      if (!(error instanceof ApiError && error.code === 'REAUTHENTICATION_REQUIRED')) throw error;
      return new Promise<T | undefined>((resolve, reject) => {
        pending.current = { retry: () => { action().then(resolve, reject); }, cancel: () => resolve(undefined) };
        setOpen(true);
      });
    }
  }, []);

  const dialog = (
    <ReauthDialog open={open}
      onConfirmed={() => { setOpen(false); pending.current?.retry(); pending.current = null; }}
      onCancel={() => { setOpen(false); pending.current?.cancel(); pending.current = null; }} />
  );
  return { perform, dialog };
}
